import { CheckCircleIcon, CircleNotchIcon, LinkedinLogoIcon } from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";

type Props = {
  elapsedMilliseconds: number;
};

const PIPELINE_STEPS = [
  {
    title: "Connexion sécurisée",
    description: "Transmission de votre profil au service d’import.",
  },
  {
    title: "Extraction du profil",
    description: "Récupération de vos expériences, formations et compétences.",
  },
  {
    title: "Organisation du CV",
    description: "Structuration des informations pour le builder.",
  },
];

const getCurrentStep = (elapsedMilliseconds: number) => {
  if (elapsedMilliseconds < 8_000) {
    return 0;
  }

  if (elapsedMilliseconds < 45_000) {
    return 1;
  }

  return 2;
};

export const LinkedInProgressStep = ({ elapsedMilliseconds }: Props) => {
  const currentStep = getCurrentStep(elapsedMilliseconds);

  return (
    <section className="mx-auto w-full max-w-xl py-8 sm:py-12">
      <header className="text-center">
        <span className="relative mx-auto grid size-16 place-items-center rounded-2xl bg-[#0A66C2]/10 text-[#0A66C2]">
          <span className="absolute inset-0 animate-ping rounded-2xl bg-[#0A66C2]/10" />

          <LinkedinLogoIcon aria-hidden size={32} weight="fill" className="relative" />
        </span>

        <p className="mt-5 text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-600">
          Import en cours
        </p>

        <h1 className="mt-1 text-2xl font-bold tracking-tight text-[#071451] sm:text-3xl">
          Nous préparons votre CV
        </h1>

        <p className="mx-auto mt-2 max-w-md text-xs leading-5 text-slate-600 sm:text-sm">
          Gardez cette fenêtre ouverte pendant que nous transformons votre profil LinkedIn.
        </p>
      </header>

      <ol aria-label="Progression de l’import LinkedIn" className="mt-8 space-y-3">
        {PIPELINE_STEPS.map((step, index) => {
          const completed = index < currentStep;

          const active = index === currentStep;

          return (
            <li
              key={step.title}
              aria-current={active ? "step" : undefined}
              className={cn(
                "flex items-start gap-3 rounded-2xl border p-4 transition",
                active && "border-indigo-300 bg-indigo-50/60 shadow-sm",
                completed && "border-emerald-200 bg-emerald-50/50",
                !active && !completed && "border-slate-200 bg-white",
              )}
            >
              <span
                className={cn(
                  "grid size-8 shrink-0 place-items-center rounded-full border",
                  completed && "border-emerald-500 bg-emerald-500 text-white",
                  active && "border-indigo-500 bg-white text-indigo-600",
                  !active && !completed && "border-slate-200 bg-slate-50 text-slate-400",
                )}
              >
                {completed ? (
                  <CheckCircleIcon aria-hidden size={18} weight="fill" />
                ) : active ? (
                  <CircleNotchIcon aria-hidden size={17} className="animate-spin" />
                ) : (
                  <span className="size-2 rounded-full bg-current" />
                )}
              </span>

              <div className="min-w-0">
                <h2
                  className={cn(
                    "text-xs font-bold sm:text-sm",
                    completed && "text-emerald-800",
                    active && "text-indigo-800",
                    !active && !completed && "text-slate-500",
                  )}
                >
                  {step.title}
                </h2>

                <p className="mt-1 text-[10px] leading-4 text-slate-500 sm:text-xs">
                  {step.description}
                </p>
              </div>
            </li>
          );
        })}
      </ol>

      <div className="mt-6 overflow-hidden rounded-full bg-indigo-50">
        <div
          className="h-2 rounded-full bg-gradient-to-r from-indigo-700 via-blue-600 to-emerald-500 transition-all duration-700"
          style={{
            width: `${currentStep === 0 ? 20 : currentStep === 1 ? 58 : 88}%`,
          }}
        />
      </div>

      <p className="mt-4 text-center text-[10px] leading-4 text-slate-500 sm:text-xs">
        Cette opération prend généralement entre 20 et 60 secondes.
      </p>
    </section>
  );
};
